// src/pages/DifferentialExpression/hooks/useGroupsManager.js

import { useState, useCallback } from "react";
import { autoLabelGroups as autoLabelGroupsUtil } from "../../../utils/groupUtils";

/* ------------------------------------------------------------------ *
 * HELPER FUNCTIONS FOR SIMILAR NAME GROUPING *
 * ------------------------------------------------------------------ */

function removeRepWithNumber(str) {
  return str.replace(/_?rep\d+/gi, "");
}

function removeStandaloneNumber(str) {
  return str
    .replace(/(^|_)\d+(_|$)/g, "$1$2")
    .replace(/__/g, "_")
    .replace(/^_|_$/g, "");
}

function groupBySimilarNames(files) {
  const keys = Object.keys(files);
  var groups = {};

  // This will be set to true if any two files are grouped together
  var any_group = false;
  for (let i = 0; i < keys.length; i++) {
    const id = keys[i];
    const name = files[id];
    const stripped_name = removeRepWithNumber(name);
    if (stripped_name in groups) {
      groups[stripped_name].push(id);
      any_group = true;
    } else {
      groups[stripped_name] = [id];
    }
  }

  if (!any_group) {
    // If it's failed to make any group at this stage do again but strip all standalone numbers
    groups = {};
    for (let i = 0; i < keys.length; i++) {
      const id = keys[i];
      const name = files[id];
      const stripped_name = removeStandaloneNumber(name);
      if (stripped_name in groups) {
        groups[stripped_name].push(id);
        any_group = true;
      } else {
        groups[stripped_name] = [id];
      }
    }
  }

  return groups;
}

/* ------------------------------------------------------------------ *
 * CONSTANTS *
 * ------------------------------------------------------------------ */

const requiredGroupIds = ["riboseq1", "riboseq2", "rnaseq1", "rnaseq2"];

const defaultNames = {
      riboseq1: "Ribo-Seq Condition 1",
      riboseq2: "Ribo-Seq Condition 2",
      rnaseq1: "RNA-Seq Condition 1",
      rnaseq2: "RNA-Seq Condition 2",
};

/* ------------------------------------------------------------------ *
 * CORE HOOK *
 * ------------------------------------------------------------------ */

export const useGroupsManager = () => {
  /* ------------------------ INITIAL STATE ------------------------- */
  const [groups, setGroups] = useState([
    {
      id: "riboseq1",
      name: "Ribo-Seq Condition 1",
      color: "#1a6d51",
      files: [],
      label: "Ribo-Seq Condition 1",
    },
    {
      id: "riboseq2",
      name: "Ribo-Seq Condition 2",
      color: "#dc3545",
      files: [],
      label: "Ribo-Seq Condition 2",
    },
    {
      id: "rnaseq1",
      name: "RNA-Seq Condition 1",
      color: "#0d6efd",
      files: [],
      label: "RNA-Seq Condition 1",
    },
    {
      id: "rnaseq2",
      name: "RNA-Seq Condition 2",
      color: "#fd7e14",
      files: [],
      label: "RNA-Seq Condition 2",
    },
  ]);

  /* ------------------------- FILE HELPERS ------------------------- */
  // Add one or many files to the target group and ensure they are removed from every other group
  const addFilesToGroup = useCallback((groupId, fileIds) => {
    const fileIdNumbers = fileIds.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    setGroups((prev) =>
      prev.map((g) => {
        if (g.id === groupId) {
          const newFiles = fileIdNumbers.filter((id) => !g.files.includes(id));
          return { ...g, files: [...g.files, ...newFiles] };
        }
        // remove the incoming fileIds from every other group
        return {
          ...g,
          files: g.files.filter((id) => !fileIdNumbers.includes(id)),
        };
      })
    );
  }, []);

  // Remove files from a specific group
  const removeFilesFromGroup = useCallback((groupId, fileIds) => {
    const fileIdNumbers = fileIds.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, files: g.files.filter((id) => !fileIdNumbers.includes(id)) }
          : g
      )
    );
  }, []);

  // Empty a single group
  const clearGroup = useCallback((groupId) => {
    const defaultName = defaultNames[groupId]

    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, files: [] } : g))
    );
    updateGroupName(groupId, defaultName);
  }, []);

  // Empty every group and reset to default names
  const clearAllGroups = useCallback(() => {
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        files: [],
        name: defaultNames[g.id] || g.name,
        label: defaultNames[g.id] || g.label,
      }))
    );
  }, []);

  // Update group name and synchronize Ribo-Seq with RNA-Seq labels
  const updateGroupName = useCallback((groupId, newName) => {
    setGroups((prev) => {
      return prev.map((g) => {
        if (g.id === groupId) {
          return { ...g, name: newName, label: newName };
        }
        return g;
      });
    });
  }, []);

  /* ------------------------- AUTO-FILL (REMOVED) ---------------------------- */
  // Auto-fill removed as requested

  /* ------------------------- AUTO-LABEL --------------------------- */
  const autoLabelGroups = useCallback(
    (files) => {
      if (!files || files.length === 0) return;

      // Use the common utility for auto-labeling
      const updatedGroups = autoLabelGroupsUtil(groups, files);
      setGroups(updatedGroups);
    },
    [groups]
  );

  /* ----------------------- VALIDATION ----------------------------- */
  const validateGroupsForDE = useCallback(() => {
    const errors = [];

    const riboGroup1 = groups.find((g) => g.id === "riboseq1");
    const riboGroup2 = groups.find((g) => g.id === "riboseq2");
    const rnaGroup1 = groups.find((g) => g.id === "rnaseq1");
    const rnaGroup2 = groups.find((g) => g.id === "rnaseq2");

    const hasRiboFiles =
      riboGroup1?.files.length > 0 && riboGroup2?.files.length > 0;
    const hasRnaFiles =
      rnaGroup1?.files.length > 0 && rnaGroup2?.files.length > 0;

    // Check if we have at least one complete set (either Ribo-Seq or RNA-Seq)
    if (!hasRiboFiles && !hasRnaFiles) {
      errors.push(
        "Please fill either both Ribo-Seq groups OR both RNA-Seq groups (or all 4 groups)"
      );
    }

    // Validate individual groups if they are partially filled
    if (riboGroup1?.files.length > 0 !== riboGroup2?.files.length > 0) {
      errors.push(
        "Both Ribo-Seq conditions must have files, or both must be empty"
      );
    }

    if (rnaGroup1?.files.length > 0 !== rnaGroup2?.files.length > 0) {
      errors.push(
        "Both RNA-Seq conditions must have files, or both must be empty"
      );
    }

    const groupsToCheck = [
      { label: "Ribo-Seq 1", group: riboGroup1 },
      { label: "Ribo-Seq 2", group: riboGroup2 },
      { label: "RNA-Seq 1", group: rnaGroup1 },
      { label: "RNA-Seq 2", group: rnaGroup2 },
    ];

    groupsToCheck.forEach(({ label, group }) => {
      // If the group exists and has exactly 1 file, it's an error
      if (group?.files?.length === 1) {
        errors.push(`${label} has only 1 replicate. At least 2 replicates are required for differential expression.`);
      }
    });





    return {
      isValid: errors.length === 0,
      errors,
      groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      hasRiboSeq: hasRiboFiles,
      hasRnaSeq: hasRnaFiles,
    };
  }, [groups]);

  /* --------------------- UTILITY PAYLOADS ------------------------- */
  const getMasterFileDict = useCallback(
    (allFiles = []) => {
      const dict = {
        riboseq1: { file_ids: [], file_names: [] },
        riboseq2: { file_ids: [], file_names: [] },
        rnaseq1: { file_ids: [], file_names: [] },
        rnaseq2: { file_ids: [], file_names: [] },
      };

      // Enhanced helper function to get file names by IDs with robust fallback
      const getFileNames = (fileIds) => {
        return fileIds.map((id) => {
          // Try to find the file in the provided allFiles array
          let file = allFiles.find((f) => f.file_id === id);

          if (file && file.file_name) {
            return file.file_name;
          }

          // If not found in allFiles, try to get from a global context if available
          // This helps preserve names when switching sequence types
          if (window.globalFileCache) {
            const cachedFile = window.globalFileCache.find(
              (f) => f.file_id === id
            );
            if (cachedFile && cachedFile.file_name) {
              return cachedFile.file_name;
            }
          }

          // Fallback to descriptive name
          return `file_${id}`;
        });
      };

      groups.forEach((g) => {
        if (g.files.length > 0) {
          dict[g.id] = {
            file_ids: g.files,
            file_names: getFileNames(g.files),
          };
        }
      });

      return dict;
    },
    [groups]
  );
  const getGroupsSummary = useCallback(() => {
    return {
      totalGroups: groups.length,
      groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      totalFilesAssigned: groups.reduce((s, g) => s + g.files.length, 0),
      groupDetails: groups.map((g) => ({
        id: g.id,
        name: g.name,
        color: g.color,
        fileCount: g.files.length,
      })),
    };
  }, [groups]);

  /* ------------------------- EXPORT ------------------------------- */
  return {
    /* state */
    groups,
    setGroups,

    /* file ops */
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    clearAllGroups,
    updateGroupName,

    /* auto helpers */
    autoLabelGroups,

    /* utilities */
    getMasterFileDict,
    validateGroupsForDE,
    getGroupsSummary,

    /* derived counters */
    totalGroups: groups.length,
    groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
    totalFilesAssigned: groups.reduce((s, g) => s + g.files.length, 0),
  };
};
